import { useEffect, useMemo, useState } from "react";
import API_BASE_URL from "../config/api";
import "./RealEstateBusinessApp.css";

function apiUrl(path) {
  return `${API_BASE_URL}${path}`;
}

async function fetchJson(path, options = {}) {
  const response = await fetch(apiUrl(path), {
    credentials: "include",
    ...options,
    headers: {
      ...(options.body ? { "Content-Type": "application/json" } : {}),
      ...(options.headers || {}),
    },
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(data.error || data.message || `Request failed with HTTP ${response.status}`);
    error.response = data;
    throw error;
  }
  return data;
}

function dollars(value) {
  const number = Number(value);
  if (!Number.isFinite(number)) return "-";
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(number);
}

function percent(value) {
  const number = Number(value);
  if (!Number.isFinite(number)) return "-";
  return `${(number * 100).toFixed(Math.abs(number) < 0.1 ? 1 : 0)}%`;
}

function statusLabel(value) {
  return String(value || "")
    .split("_")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ") || "-";
}

function formatDate(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return new Intl.DateTimeFormat("en-US", { dateStyle: "medium", timeStyle: "short" }).format(date);
}

function candidateKey(candidate, index = 0) {
  return candidate.candidateId || `${candidate.scanId || "candidate"}-${candidate.address?.full || index}`;
}

function CandidateUnderwritingSummary({ candidate }) {
  const underwriting = candidate?.underwriting || {};
  const price = candidate?.listPrice || underwriting.acquisitionPrice;
  const spread = Number(underwriting.spread);
  const spreadClass = Number.isFinite(spread) ? (spread >= 0 ? "is-positive" : "is-negative") : "";
  return (
    <div className="re-deal-math-grid">
      <span><small>Price</small><strong>{dollars(price)}</strong></span>
      <span><small>ARV</small><strong>{dollars(underwriting.arv)}</strong><em>{underwriting.arvConfidence || "unverified"}</em></span>
      <span><small>Rehab</small><strong>{dollars(underwriting.rehabCost)}</strong></span>
      <span><small>MAO</small><strong>{dollars(underwriting.maxAllowableOffer)}</strong></span>
      <span className={spreadClass}><small>Spread</small><strong>{dollars(underwriting.spread)}</strong></span>
    </div>
  );
}

function ReportCandidateCards({ candidates = [], onCreateJob, busy, emptyMessage = "No candidates returned yet." }) {
  if (!candidates.length) return <p className="re-muted">{emptyMessage}</p>;
  return (
    <div className="re-candidate-list re-hunt-list">
      {candidates.map((candidate, index) => (
        <article key={candidateKey(candidate, index)}>
          <div>
            <strong>{candidate.address?.full || candidate.candidateId}</strong>
            <span>{candidate.dealType} - score {candidate.score} - {candidate.source}</span>
          </div>
          <CandidateUnderwritingSummary candidate={candidate} />
          <p>{candidate.recommendedAction}</p>
          <footer>{candidate.status ? `${candidate.status} - ` : ""}{candidate.market?.type ? `${candidate.market.type}: ${candidate.market.value}` : "Lead"}</footer>
          {candidate.underwriting?.warnings?.length > 0 && (
            <details className="re-details compact">
              <summary>Underwriting warnings</summary>
              <ul>
                {candidate.underwriting.warnings.map((warning) => <li key={warning}>{warning}</li>)}
              </ul>
            </details>
          )}
          {onCreateJob && <button type="button" className="re-small-button" onClick={() => onCreateJob(candidate)} disabled={busy}>Create job</button>}
        </article>
      ))}
    </div>
  );
}

function LoginPage({ onLogin }) {
  const [username, setUsername] = useState("admin");
  const [password, setPassword] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);

  async function submit(event) {
    event.preventDefault();
    setBusy(true);
    setMessage("");
    try {
      await fetchJson("/auth/site-login", {
        method: "POST",
        body: JSON.stringify({ username, password }),
      });
      const session = await fetchJson("/auth/site-session");
      if (!session.authenticated) throw new Error("Login accepted but admin session cookie was not available.");
      onLogin(true);
    } catch (error) {
      setMessage(error.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="re-login-shell">
      <section className="re-login-card">
        <div className="re-brand-mark">RE</div>
        <h1>Interpres Real Estate</h1>
        <p>Private fix-and-flip, wholesale, scope-of-work, and material-order cockpit.</p>
        <form onSubmit={submit} className="re-form-stack">
          <label>Username<input value={username} onChange={(event) => setUsername(event.target.value)} /></label>
          <label>Password<input type="password" value={password} onChange={(event) => setPassword(event.target.value)} autoFocus /></label>
          <button type="submit" disabled={busy}>{busy ? "Unlocking..." : "Unlock Real Estate Business"}</button>
        </form>
        {message && <p className="re-error">{message}</p>}
      </section>
    </main>
  );
}

function ProviderStatus({ providers = [] }) {
  return (
    <div className="re-provider-grid">
      {providers.map((provider) => (
        <article className="re-provider-card" key={provider.provider}>
          <div>
            <strong>{provider.displayName}</strong>
            <span className={provider.configured ? "re-status-good" : "re-status-warn"}>{provider.configured ? "Configured" : "Needs credentials"}</span>
          </div>
          <p>{provider.notes}</p>
          <dl>
            <div><dt>Catalog</dt><dd>{provider.productSearch ? "Yes" : "Not yet"}</dd></div>
            <div><dt>Cart draft</dt><dd>{provider.cartDraft ? "Yes" : "Adapter slot"}</dd></div>
            <div><dt>Live submit</dt><dd>{provider.orderSubmit ? "Enabled" : "Blocked"}</dd></div>
          </dl>
        </article>
      ))}
    </div>
  );
}

function ScopeLineRow({ line, onUpdateLine, onApproveMaterial }) {
  const selected = line.selectedMaterial || line.recommendedMaterialOptions?.[0] || null;
  const [draft, setDraft] = useState({
    quantity: line.quantity ?? "",
    materialOrderQuantity: line.materialOrderQuantity ?? "",
    materialUnitCost: line.materialUnitCost ?? "",
    laborUnitCost: line.laborUnitCost ?? "",
    notes: line.notes || "",
  });

  useEffect(() => {
    setDraft({
      quantity: line.quantity ?? "",
      materialOrderQuantity: line.materialOrderQuantity ?? "",
      materialUnitCost: line.materialUnitCost ?? "",
      laborUnitCost: line.laborUnitCost ?? "",
      notes: line.notes || "",
    });
  }, [line]);

  function updateDraft(key, value) {
    setDraft((current) => ({ ...current, [key]: value }));
  }

  return (
    <tr>
      <td><span className="re-chip">{line.phase}</span></td>
      <td>
        <strong>{line.task}</strong>
        {line.requiresFieldMeasurement && <em>Field measurement required</em>}
        <textarea value={draft.notes} onChange={(event) => updateDraft("notes", event.target.value)} rows={2} />
        <button type="button" className="re-small-button" onClick={() => onUpdateLine(line.scopeLineId, draft)}>Save line</button>
      </td>
      <td>
        <div className="re-mini-fields">
          <input value={draft.materialOrderQuantity} onChange={(event) => updateDraft("materialOrderQuantity", event.target.value)} />
          <small>{line.unit}</small>
        </div>
      </td>
      <td>
        <span>{line.recommendedSearchQuery || "-"}</span>
        {selected && (
          <small>
            {selected.provider} - item {selected.providerSku || selected.providerProductId || "not selected"}
          </small>
        )}
        {selected?.approval && <em>Approved {formatDate(selected.approval.approvedAt)}</em>}
        <button type="button" className="re-small-button secondary" disabled={!selected} onClick={() => onApproveMaterial(line.scopeLineId)}>Approve item</button>
      </td>
      <td><input value={draft.materialUnitCost} onChange={(event) => updateDraft("materialUnitCost", event.target.value)} /></td>
      <td><input value={draft.laborUnitCost} onChange={(event) => updateDraft("laborUnitCost", event.target.value)} /></td>
      <td>{dollars(line.estimatedLineTotal)}</td>
    </tr>
  );
}

function ScopeTable({ scope, onUpdateLine, onApproveMaterial }) {
  const lines = scope?.lines || [];
  if (!lines.length) return <p className="re-muted">No scope generated yet.</p>;
  return (
    <div className="re-table-wrap">
      <table className="re-table">
        <thead>
          <tr>
            <th>Phase</th>
            <th>Task</th>
            <th>Qty</th>
            <th>Search / item</th>
            <th>Material</th>
            <th>Labor</th>
            <th>Total</th>
          </tr>
        </thead>
        <tbody>
          {lines.map((line) => {
            const selected = line.selectedMaterial || line.recommendedMaterialOptions?.[0] || null;
            return (
              <tr key={line.scopeLineId}>
                <td><span className="re-chip">{line.phase}</span></td>
                <td>
                  <strong>{line.task}</strong>
                  {line.notes && <small>{line.notes}</small>}
                  {line.requiresFieldMeasurement && <em>Field measurement required</em>}
                </td>
                <td>{line.materialOrderQuantity || line.quantity} {line.unit}</td>
                <td>
                  <span>{line.recommendedSearchQuery || "-"}</span>
                  {selected && (
                    <small>
                      {selected.provider} · item {selected.providerSku || selected.providerProductId || "not selected"}
                    </small>
                  )}
                </td>
                <td>{dollars(line.estimatedMaterialCost)}</td>
                <td>{dollars(line.estimatedLaborCost)}</td>
                <td>{dollars(line.estimatedLineTotal)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function SummaryCards({ scope, dealMath }) {
  const summary = scope?.summary || {};
  return (
    <div className="re-card-grid four">
      <article className="re-metric"><span>ARV</span><strong>{dollars(dealMath?.arv)}</strong></article>
      <article className="re-metric"><span>Repairs</span><strong>{dollars(dealMath?.repairs || summary.estimatedTotal)}</strong></article>
      <article className="re-metric"><span>MAO</span><strong>{dollars(dealMath?.maxAllowableOffer)}</strong></article>
      <article className="re-metric"><span>Scope total</span><strong>{dollars(summary.estimatedTotal)}</strong></article>
    </div>
  );
}

function RoomTakeoff({ scope }) {
  const rooms = scope?.roomTakeoff?.rooms || [];
  if (!rooms.length) return null;
  return (
    <div className="re-room-grid">
      {rooms.map((room) => (
        <article key={room.roomId}>
          <strong>{room.name}</strong>
          <span>{room.type} - {room.estimatedSqft || "-"} sqft</span>
          <em>{room.condition || "inspect"}</em>
        </article>
      ))}
    </div>
  );
}

function ScopeEditorTable({ scope, onUpdateLine, onApproveMaterial }) {
  const lines = scope?.lines || [];
  if (!lines.length) return null;
  return (
    <div className="re-table-wrap re-edit-table">
      <table className="re-table">
        <thead>
          <tr>
            <th>Phase</th>
            <th>Task / notes</th>
            <th>Qty</th>
            <th>Approved item</th>
            <th>Material unit</th>
            <th>Labor unit</th>
            <th>Total</th>
          </tr>
        </thead>
        <tbody>
          {lines.map((line) => <ScopeLineRow key={line.scopeLineId} line={line} onUpdateLine={onUpdateLine} onApproveMaterial={onApproveMaterial} />)}
        </tbody>
      </table>
    </div>
  );
}

function ProjectList({ projects = [], selectedProjectId, onSelect }) {
  if (!projects.length) return <p className="re-muted">No saved real-estate projects yet.</p>;
  return (
    <div className="re-project-list">
      {projects.map((project) => (
        <button
          type="button"
          key={project.projectId}
          className={selectedProjectId === project.projectId ? "is-active" : ""}
          onClick={() => onSelect(project)}
        >
          <strong>{project.address?.full || project.projectId}</strong>
          <span>{project.status} · {formatDate(project.createdAt)}</span>
          <em>Repairs {dollars(project.dealMath?.repairs)} · MAO {dollars(project.dealMath?.maxAllowableOffer)}</em>
        </button>
      ))}
    </div>
  );
}

function CandidateList({ scan }) {
  const candidates = scan?.candidates || [];
  if (!scan) return <p className="re-muted">Run a market scan to pull HUD, USDA, and configured market-source candidates.</p>;
  if (!candidates.length) {
    return (
      <div>
        <p className="re-muted">No candidates returned from the currently connected live sources.</p>
        {(scan.warnings || []).map((warning) => <p className="re-warning" key={warning}>{warning}</p>)}
      </div>
    );
  }
  return <ReportCandidateCards candidates={candidates} />;
}

function WorkflowChecklist({ project, onUpdateItem }) {
  const items = project?.workflow?.items || [];
  if (!items.length) return <p className="re-muted">No workflow checklist generated for this job yet.</p>;
  return (
    <div className="re-workflow-list">
      {items.map((item) => (
        <article key={item.itemId} className={item.completed ? "is-complete" : ""}>
          <label>
            <input
              type="checkbox"
              checked={Boolean(item.completed)}
              onChange={(event) => onUpdateItem(item.itemId, { completed: event.target.checked })}
            />
            <span>{item.order}. {item.title}</span>
          </label>
          <p>{item.description}</p>
          {item.notes && <em>{item.notes}</em>}
        </article>
      ))}
    </div>
  );
}

function DocumentLibrary({ library }) {
  const documents = library?.documents || [];
  return (
    <section id="documents" className="re-panel">
      <div className="re-section-head">
        <div>
          <h2>Documents + contracts</h2>
          <p>Operator packets and starter templates for the first Texas deal. Anything that creates legal obligations should be reviewed before signature.</p>
        </div>
      </div>
      {library?.policy?.note && <p className="re-warning">{library.policy.note}</p>}
      <div className="re-document-grid">
        {documents.map((document) => (
          <article key={document.documentKey}>
            <span className="re-chip">{document.category}</span>
            <h3>{document.title}</h3>
            <p>{document.description}</p>
            <dl>
              <div><dt>Use</dt><dd>{document.whenToUse}</dd></div>
              <div><dt>Status</dt><dd>{document.status}</dd></div>
            </dl>
            <a href={apiUrl(`/api/real-estate/documents/${encodeURIComponent(document.documentKey)}/export`)} target="_blank" rel="noreferrer">Open / download</a>
          </article>
        ))}
      </div>
    </section>
  );
}

function TexasDealHunt({ report, onRun, onCreateJob, busy }) {
  const candidates = report?.candidates || [];
  const links = report?.manualSourceLinks || [];
  return (
    <section id="texas-hunt" className="re-panel">
      <div className="re-section-head">
        <div>
          <h2>Texas first-deal hunt</h2>
          <p>Daily RSI-oversold-style scan: look for properties cheap enough that title, repair, and resale risk still leave room.</p>
        </div>
        <div className="re-actions">
          <button type="button" disabled={busy} onClick={onRun}>{busy ? "Scanning..." : "Run Texas deal hunt"}</button>
        </div>
      </div>
      {report ? (
        <>
          <div className="re-card-grid four">
            <article className="re-metric"><span>Markets</span><strong>{report.marketCount}</strong></article>
            <article className="re-metric"><span>Candidates</span><strong>{report.candidateCount}</strong></article>
            <article className="re-metric"><span>Manual sources</span><strong>{links.length}</strong></article>
            <article className="re-metric"><span>Completed</span><strong>{formatDate(report.completedAt)}</strong></article>
          </div>
          <p className="re-muted">{report.operatingRule}</p>
          <ReportCandidateCards
            candidates={candidates}
            onCreateJob={onCreateJob}
            busy={busy}
            emptyMessage="No live-source candidates came back. Use the manual source links below and create jobs from addresses that look deeply mispriced."
          />
        </>
      ) : (
        <p className="re-muted">Run this every morning while hunting the first Texas deal. It combines pullable public sources with public dataset discovery and manual auction/tax-sale links.</p>
      )}
      <details className="re-details" open>
        <summary>Texas manual source links</summary>
        <div className="re-source-grid">
          {links.map((link) => (
            <article className="re-source" key={link.sourceKey}>
              <strong>{link.displayName}</strong>
              <p>{link.bestUse}</p>
              <em>{link.action}</em>
              <a href={link.sourceUrl} target="_blank" rel="noreferrer">Open source</a>
            </article>
          ))}
        </div>
      </details>
    </section>
  );
}

function OwnerResearchPanel({ report, reports = [], sourcePlan, watchlist = [], form, onFormChange, onRun, onSaveMonitor, onRunWatchlist, onCreateJob, busy }) {
  const manualSources = report?.manualSources || sourcePlan?.manualSources || [];
  const links = report?.queryLinks || [];
  const findings = report?.findings || [];
  const taxSaleRisk = report?.taxSaleRisk || null;
  return (
    <section id="owner-research" className="re-panel">
      <div className="re-section-head">
        <div>
          <h2>Owner research</h2>
          <p>Cross-reference owner, tax, deed, probate, obituary, and tax-sale risk clues before bidding or contacting anyone.</p>
        </div>
        <div className="re-actions">
          <button type="button" disabled={busy} onClick={onRunWatchlist}>{busy ? "Running..." : "Run monitors"}</button>
        </div>
      </div>
      <form className="re-lead-form owner" onSubmit={onRun}>
        <label>Street address<input value={form.street} onChange={(event) => onFormChange("street", event.target.value)} placeholder="209 W Curtis Ave" /></label>
        <label>City<input value={form.city} onChange={(event) => onFormChange("city", event.target.value)} placeholder="Pasadena" /></label>
        <label>State<input value={form.state} onChange={(event) => onFormChange("state", event.target.value)} placeholder="TX" /></label>
        <label>ZIP<input value={form.zip} onChange={(event) => onFormChange("zip", event.target.value)} placeholder="77502" /></label>
        <label>Owner name<input value={form.ownerName} onChange={(event) => onFormChange("ownerName", event.target.value)} placeholder="Assessed owner" /></label>
        <label>Tax account<input value={form.taxAccount} onChange={(event) => onFormChange("taxAccount", event.target.value)} placeholder="0700590000136" /></label>
        <label>Taxes owed<input value={form.taxesOwed} onChange={(event) => onFormChange("taxesOwed", event.target.value)} placeholder="9010.77" /></label>
        <label>Source URL<input value={form.sourceUrl} onChange={(event) => onFormChange("sourceUrl", event.target.value)} placeholder="Tax sale/property detail URL" /></label>
        <button type="submit" disabled={busy}>{busy ? "Researching..." : "Run owner research"}</button>
        <button type="button" disabled={busy} onClick={onSaveMonitor}>Save monitor</button>
      </form>

      <div className="re-card-grid four re-sub-metrics">
        <article className="re-metric"><span>Configured feeds</span><strong>{sourcePlan?.configuredSourceCount || 0}</strong></article>
        <article className="re-metric"><span>Watched leads</span><strong>{watchlist.length}</strong></article>
        <article className="re-metric"><span>Saved reports</span><strong>{reports.length}</strong></article>
        <article className="re-metric"><span>Policy</span><strong>Verify</strong></article>
      </div>

      {report ? (
        <>
          <div className="re-card-grid four re-sub-metrics">
            <article className="re-metric"><span>Opportunity</span><strong>{report.summary?.opportunityScore ?? "-"}</strong></article>
            <article className="re-metric"><span>Complexity</span><strong>{report.summary?.complexityRiskScore ?? "-"}</strong></article>
            <article className="re-metric"><span>Death status</span><strong>{report.summary?.ownerDeathStatus || "UNCONFIRMED"}</strong></article>
            <article className="re-metric"><span>Tax status</span><strong>{report.summary?.taxDistressStatus || "UNKNOWN"}</strong></article>
          </div>
          <article className="re-source">
            <strong>{report.address?.full}</strong>
            <span>{report.ownerName || "Owner not entered"} {report.taxAccount ? `- Tax account ${report.taxAccount}` : ""}</span>
            <p>{report.summary?.nextOfKinPolicy}</p>
            <div className="re-actions">
              <button type="button" disabled={busy} onClick={() => onCreateJob(report)}>Create job from report</button>
            </div>
          </article>

          {taxSaleRisk?.applies && (
            <details className="re-details" open>
              <summary>Texas tax-sale buyer risk</summary>
              <p className="re-muted">{taxSaleRisk.summary}</p>
              <div className="re-source-grid">
                <article className="re-source"><strong>Redemption</strong>{taxSaleRisk.redemption?.map((item) => <span key={item}>{item}</span>)}</article>
                <article className="re-source"><strong>Possession</strong>{taxSaleRisk.possession?.map((item) => <span key={item}>{item}</span>)}</article>
                <article className="re-source"><strong>Title</strong>{taxSaleRisk.title?.map((item) => <span key={item}>{item}</span>)}</article>
              </div>
            </details>
          )}

          <div className="re-warning-list">
            {(report.warnings || []).map((warning) => <span key={warning}>{warning}</span>)}
          </div>

          <details className="re-details" open>
            <summary>Research links</summary>
            <div className="re-source-grid">
              {[...links, ...manualSources].map((link) => (
                <article className="re-source" key={`${link.category || link.sourceKey}-${link.label || link.displayName}`}>
                  <strong>{link.label || link.displayName}</strong>
                  <p>{link.bestUse || link.category}</p>
                  <a href={link.url || link.sourceUrl} target="_blank" rel="noreferrer">Open source</a>
                </article>
              ))}
            </div>
          </details>

          <details className="re-details" open>
            <summary>Configured-source findings</summary>
            <div className="re-discovery-list">
              {findings.map((finding) => (
                <article key={finding.findingId}>
                  <strong>{finding.title}</strong>
                  <span>{finding.source} - {finding.category} - {finding.confidence}</span>
                  {finding.amount != null && <p>Amount: {dollars(finding.amount)}</p>}
                  {finding.ownerName && <p>Named party: {finding.ownerName}</p>}
                  {finding.url && <a href={finding.url} target="_blank" rel="noreferrer">Open record</a>}
                </article>
              ))}
              {!findings.length && <p className="re-muted">No configured API/source findings yet. Use the research links, then add official feeds through env when available.</p>}
            </div>
          </details>
        </>
      ) : (
        <p className="re-muted">Run a report from an address, then use the official links to verify owner, death/probate clues, taxes owed, title risk, and possession risk.</p>
      )}

      {watchlist.length > 0 && (
        <details className="re-details">
          <summary>Watched owner leads</summary>
          <div className="re-target-list">
            {watchlist.map((watch) => (
              <article key={watch.watchId}>
                <strong>{watch.address?.full}</strong>
                <span>{watch.ownerName || "Owner not entered"} {watch.taxAccount ? `- ${watch.taxAccount}` : ""}</span>
                <em>{watch.lastReportId ? `Last report ${watch.lastReportId}` : "No report run yet"}</em>
              </article>
            ))}
          </div>
        </details>
      )}
    </section>
  );
}

function TaxSaleAuctionDesk({ form, onFormChange, plan, plans = [], onAnalyze, onSave, onCreateProject, onStatusChange, onLoadPlan, busy }) {
  const redemptionScenarios = plan?.redemptionScenarios || [];
  const warnings = plan?.warnings || [];
  const lienItems = plan?.lienRisk?.items || [];
  const lienStatusOptions = [
    ["unknown", "Unknown / not checked"],
    ["not_found", "Not found / released"],
    ["joined_in_tax_suit", "Joined in tax suit"],
    ["possible_survival", "Possible survival"],
    ["survives_confirmed", "Payoff required"],
  ];
  return (
    <section id="tax-sale-desk" className="re-panel tax-sale-desk">
      <div className="re-section-head">
        <div>
          <h2>Tax sale auction desk</h2>
          <p>Separate auction underwriting for Harris/Texas tax-sale deals where redemption, title, possession, certified funds, and bid caps change the economics.</p>
        </div>
        <div className="re-actions">
          <button type="button" disabled={busy} onClick={onSave}>{busy ? "Saving..." : "Save auction plan"}</button>
        </div>
      </div>

      <form className="re-auction-form" onSubmit={onAnalyze}>
        <label>Street<input value={form.street} onChange={(event) => onFormChange("street", event.target.value)} placeholder="209 W Curtis Ave" /></label>
        <label>City<input value={form.city} onChange={(event) => onFormChange("city", event.target.value)} /></label>
        <label>State<input value={form.state} onChange={(event) => onFormChange("state", event.target.value)} /></label>
        <label>ZIP<input value={form.zip} onChange={(event) => onFormChange("zip", event.target.value)} /></label>
        <label>County<input value={form.county} onChange={(event) => onFormChange("county", event.target.value)} /></label>
        <label>Tax account<input value={form.taxAccount} onChange={(event) => onFormChange("taxAccount", event.target.value)} /></label>
        <label>Cause #<input value={form.causeNumber} onChange={(event) => onFormChange("causeNumber", event.target.value)} /></label>
        <label>Sale date<input type="date" value={form.saleDate} onChange={(event) => onFormChange("saleDate", event.target.value)} /></label>
        <label>Redemption class<select value={form.redemptionClass} onChange={(event) => onFormChange("redemptionClass", event.target.value)}>
          <option value="unknown">Unknown / verify</option>
          <option value="non_homestead">Non-homestead</option>
          <option value="residence_homestead">Residence homestead</option>
          <option value="agricultural_use">Agricultural use</option>
          <option value="mineral_interest">Mineral interest</option>
        </select></label>
        <label>Occupancy<select value={form.occupancyStatus} onChange={(event) => onFormChange("occupancyStatus", event.target.value)}>
          <option value="unknown">Unknown</option>
          <option value="vacant">Vacant verified</option>
          <option value="occupied_owner">Owner occupied</option>
          <option value="occupied_tenant">Tenant occupied</option>
          <option value="unsafe_no_entry">Unsafe / no entry</option>
        </select></label>
        <label>Title review<select value={form.titleStatus} onChange={(event) => onFormChange("titleStatus", event.target.value)}>
          <option value="not_reviewed">Not reviewed</option>
          <option value="preliminary_clear">Preliminary clear</option>
          <option value="professional_clear">Title/attorney clear</option>
          <option value="issues_found">Issues found</option>
          <option value="blocked">Blocked / uninsurable</option>
        </select></label>
        <label>Estimated value<input value={form.estimatedMarketValue} onChange={(event) => onFormChange("estimatedMarketValue", event.target.value)} placeholder="1300000" /></label>
        <label>Minimum bid<input value={form.minimumBid} onChange={(event) => onFormChange("minimumBid", event.target.value)} placeholder="35000" /></label>
        <label>Planned max bid<input value={form.plannedBid} onChange={(event) => onFormChange("plannedBid", event.target.value)} placeholder="35000" /></label>
        <label>Target return %<input value={form.targetReturnRate} onChange={(event) => onFormChange("targetReturnRate", event.target.value)} /></label>
        <label>Repair budget<input value={form.repairBudget} onChange={(event) => onFormChange("repairBudget", event.target.value)} /></label>
        <label>Post-judgment taxes<input value={form.postJudgmentTaxes} onChange={(event) => onFormChange("postJudgmentTaxes", event.target.value)} /></label>
        <label>Title search<input value={form.titleSearchBudget} onChange={(event) => onFormChange("titleSearchBudget", event.target.value)} /></label>
        <label>Attorney<input value={form.attorneyBudget} onChange={(event) => onFormChange("attorneyBudget", event.target.value)} /></label>
        <label>Possession budget<input value={form.possessionBudget} onChange={(event) => onFormChange("possessionBudget", event.target.value)} /></label>
        <label>Holding months<input value={form.holdingMonths} onChange={(event) => onFormChange("holdingMonths", event.target.value)} /></label>
        <label>Monthly holding<input value={form.monthlyHoldingCost} onChange={(event) => onFormChange("monthlyHoldingCost", event.target.value)} /></label>
        <label>Sales cost %<input value={form.salesCostRate} onChange={(event) => onFormChange("salesCostRate", event.target.value)} /></label>
        <label>Mortgage status<select value={form.mortgageLienStatus} onChange={(event) => onFormChange("mortgageLienStatus", event.target.value)}>
          {lienStatusOptions.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
        </select></label>
        <label>Mortgage payoff<input value={form.mortgagePayoffEstimate} onChange={(event) => onFormChange("mortgagePayoffEstimate", event.target.value)} placeholder="0" /></label>
        <label>IRS lien status<select value={form.federalTaxLienStatus} onChange={(event) => onFormChange("federalTaxLienStatus", event.target.value)}>
          {lienStatusOptions.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
        </select></label>
        <label>IRS lien payoff<input value={form.federalTaxLienEstimate} onChange={(event) => onFormChange("federalTaxLienEstimate", event.target.value)} placeholder="0" /></label>
        <label>Municipal/code status<select value={form.municipalLienStatus} onChange={(event) => onFormChange("municipalLienStatus", event.target.value)}>
          {lienStatusOptions.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
        </select></label>
        <label>Municipal/code payoff<input value={form.municipalLienEstimate} onChange={(event) => onFormChange("municipalLienEstimate", event.target.value)} placeholder="0" /></label>
        <label>HOA status<select value={form.hoaLienStatus} onChange={(event) => onFormChange("hoaLienStatus", event.target.value)}>
          {lienStatusOptions.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
        </select></label>
        <label>HOA payoff<input value={form.hoaLienEstimate} onChange={(event) => onFormChange("hoaLienEstimate", event.target.value)} placeholder="0" /></label>
        <label>Judgment status<select value={form.judgmentLienStatus} onChange={(event) => onFormChange("judgmentLienStatus", event.target.value)}>
          {lienStatusOptions.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
        </select></label>
        <label>Judgment payoff<input value={form.judgmentLienEstimate} onChange={(event) => onFormChange("judgmentLienEstimate", event.target.value)} placeholder="0" /></label>
        <label>Other lien status<select value={form.otherSurvivingLienStatus} onChange={(event) => onFormChange("otherSurvivingLienStatus", event.target.value)}>
          {lienStatusOptions.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
        </select></label>
        <label>Other lien payoff<input value={form.otherSurvivingLienEstimate} onChange={(event) => onFormChange("otherSurvivingLienEstimate", event.target.value)} placeholder="0" /></label>
        <label>Source URL<input value={form.sourceUrl} onChange={(event) => onFormChange("sourceUrl", event.target.value)} /></label>
        <label>Notes<textarea value={form.notes} onChange={(event) => onFormChange("notes", event.target.value)} rows={2} /></label>
        <button type="submit" disabled={busy}>{busy ? "Calculating..." : "Analyze auction economics"}</button>
      </form>

      {plan ? (
        <>
          <div className="re-card-grid four re-sub-metrics">
            <article className="re-metric"><span>Recommended max bid</span><strong>{dollars(plan.bidDiscipline?.maxAllowableBidForExit)}</strong></article>
            <article className="re-metric"><span>Planned gap</span><strong>{dollars(plan.bidDiscipline?.plannedBidGapToMax)}</strong></article>
            <article className="re-metric"><span>Cash at risk</span><strong>{dollars(plan.costs?.cashAtRisk)}</strong></article>
            <article className="re-metric"><span>Recommendation</span><strong>{plan.bidDiscipline?.recommendation || "-"}</strong></article>
          </div>

          <div className="re-card-grid three re-sub-metrics">
            <article className="re-metric"><span>Redemption period</span><strong>{plan.redemptionRule?.periodDays || "-"} days</strong><em>{plan.redemptionRule?.label}</em></article>
            <article className="re-metric"><span>Exit ROI</span><strong>{percent(plan.exitScenario?.roi)}</strong><em>After repairs/costs/sale</em></article>
            <article className="re-metric"><span>Net exit profit</span><strong>{dollars(plan.exitScenario?.profit)}</strong><em>{dollars(plan.exitScenario?.netExitProceeds)} net proceeds</em></article>
          </div>

          <div className="re-card-grid four re-sub-metrics">
            <article className="re-metric"><span>Surviving-lien max bid</span><strong>{dollars(plan.bidDiscipline?.maxAllowableBidWithSurvivingLiens)}</strong><em>Conservative downside</em></article>
            <article className="re-metric"><span>Surviving-lien ROI</span><strong>{percent(plan.survivingLienExitScenario?.roi)}</strong><em>{dollars(plan.survivingLienExitScenario?.profit)} profit</em></article>
            <article className="re-metric"><span>Possible lien exposure</span><strong>{dollars(plan.lienRisk?.survivingLienExposure)}</strong><em>{plan.lienRisk?.reviewOpen ? "Review open" : "Review marked"}</em></article>
            <article className="re-metric"><span>Apparent equity after bid</span><strong>{dollars(plan.lienRisk?.apparentEquityAfterPlannedBid)}</strong><em>Value minus liens, taxes, bid</em></article>
          </div>

          <div className="re-source-grid">
            {redemptionScenarios.map((scenario) => (
              <article className="re-source" key={scenario.period}>
                <strong>{scenario.label}</strong>
                <span>Premium: {percent(scenario.premiumRate)} / ROI: {percent(scenario.roi)} / annualized {percent(scenario.annualizedRoi)}</span>
                <p>Payout {dollars(scenario.redemptionPayout)} - profit {dollars(scenario.profit)} - premium {dollars(scenario.redemptionPremium)}</p>
                {scenario.minimumBidToHitTarget != null && <em>Min bid to overcome fixed costs at target: {dollars(scenario.minimumBidToHitTarget)}</em>}
              </article>
            ))}
          </div>

          <details className="re-details" open>
            <summary>Fees, costs, and capital</summary>
            <div className="re-table-wrap">
              <table className="re-table compact">
                <thead><tr><th>Item</th><th>Amount</th><th>Treatment</th></tr></thead>
                <tbody>
                  <tr><td>Cash due at sale</td><td>{dollars(plan.costs?.cashDueAtSale)}</td><td>Immediate certified funds / bid cash</td></tr>
                  <tr><td>Recoverable cost bucket</td><td>{dollars(plan.costs?.recoverableCosts)}</td><td>Potential redemption base, subject to documentation/law</td></tr>
                  <tr><td>Non-recoverable risk bucket</td><td>{dollars(plan.costs?.nonRecoverableCosts)}</td><td>Underwriting drag / conservative expense bucket</td></tr>
                  <tr><td>Holding budget</td><td>{dollars(plan.costs?.holdingBudget)}</td><td>Included in non-recoverable risk bucket</td></tr>
                  <tr><td>Bid above minimum</td><td>{dollars(plan.exitScenario?.bidAboveMinimum)}</td><td>Amount planned above listed opening/minimum bid</td></tr>
                </tbody>
              </table>
            </div>
          </details>

          <details className="re-details" open>
            <summary>Title, lien, and equity review</summary>
            <div className="re-table-wrap">
              <table className="re-table compact">
                <thead><tr><th>Risk</th><th>Status</th><th>Payoff / reserve</th><th>How the desk treats it</th></tr></thead>
                <tbody>
                  {lienItems.map((item) => (
                    <tr key={item.key}>
                      <td>{item.label}</td>
                      <td>{statusLabel(item.status)}</td>
                      <td>{dollars(item.estimatedPayoff)}</td>
                      <td>{["survives_confirmed", "possible_survival", "unknown"].includes(item.status) ? "Included in surviving-lien downside when an amount is entered." : "Not included in surviving-lien payoff reserve."}</td>
                    </tr>
                  ))}
                  <tr><td>Known lien face amount</td><td>All entered liens</td><td>{dollars(plan.lienRisk?.knownLienFaceAmount)}</td><td>Used to estimate apparent equity before/after bid.</td></tr>
                  <tr><td>Title status</td><td>{statusLabel(plan.titleStatus)}</td><td>-</td><td>{plan.lienRisk?.titleProfessionallyCleared ? "Professional/title review marked clear." : "Bid gate remains cautious until title/lien treatment is verified."}</td></tr>
                </tbody>
              </table>
            </div>
            <p className="re-muted">{plan.lienRisk?.rule}</p>
          </details>

          {warnings.length > 0 && <div className="re-warning-list">{warnings.map((warning) => <span key={warning}>{warning}</span>)}</div>}

          <details className="re-details" open>
            <summary>Auction execution checklist</summary>
            <div className="re-target-list">
              {(plan.checklist || []).map((item) => (
                <article key={item.stage}>
                  <strong>{item.label}</strong>
                  <span>{item.stage} - {item.status}</span>
                </article>
              ))}
            </div>
          </details>
        </>
      ) : (
        <p className="re-muted">Enter an auction row and run the analysis. Use this page for tax-sale/deed auction economics only; normal listings and direct-sale houses stay in the regular lead/project flow.</p>
      )}

      {plans.length > 0 && (
        <details className="re-details" open>
          <summary>Saved auction portfolio</summary>
          <div className="re-target-list auction">
            {plans.map((savedPlan) => (
              <article key={savedPlan.planId}>
                <strong>{savedPlan.address?.full}</strong>
                <span>{savedPlan.county} County - {savedPlan.redemptionRule?.label} - clean max {dollars(savedPlan.bidDiscipline?.maxAllowableBidForExit)} - lien max {dollars(savedPlan.bidDiscipline?.maxAllowableBidWithSurvivingLiens)} - status {savedPlan.status}</span>
                <em>Planned bid {dollars(savedPlan.assumptions?.plannedBid)} / minimum {dollars(savedPlan.assumptions?.minimumBid)} / target {percent(savedPlan.assumptions?.targetReturnRate)}</em>
                <div className="re-actions">
                  <button type="button" disabled={busy} onClick={() => onLoadPlan(savedPlan)}>Load plan</button>
                  <select value={savedPlan.status || "watching"} onChange={(event) => onStatusChange(savedPlan.planId, event.target.value)}>
                    <option value="watching">Watching</option>
                    <option value="researching">Researching</option>
                    <option value="bid_ready">Bid ready</option>
                    <option value="bid_submitted">Bid submitted</option>
                    <option value="won">Won</option>
                    <option value="redeemed">Redeemed</option>
                    <option value="held">Held after redemption</option>
                    <option value="sold">Sold</option>
                    <option value="dead">Dead</option>
                    <option value="archived">Archived</option>
                  </select>
                  <button type="button" disabled={busy} onClick={() => onCreateProject(savedPlan.planId)}>Create project</button>
                </div>
              </article>
            ))}
          </div>
        </details>
      )}
    </section>
  );
}

function LeadSourceMap({ catalog }) {
  const sources = catalog?.nationalSources || [];
  const states = catalog?.states || [];
  const liveSources = sources.filter((source) => source.integrationStatus === "live_pull_enabled");
  const [discoveryForm, setDiscoveryForm] = useState({ query: "tax sale properties", state: "TX", city: "", county: "" });
  const [discovery, setDiscovery] = useState(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");

  async function runDiscovery(event) {
    event.preventDefault();
    setBusy(true);
    setMessage("");
    try {
      const data = await fetchJson("/api/real-estate/lead-sources/discover", {
        method: "POST",
        body: JSON.stringify({
          query: discoveryForm.query,
          state: discoveryForm.state,
          city: discoveryForm.city,
          county: discoveryForm.county,
          limit: 8,
        }),
      });
      setDiscovery(data.discovery);
    } catch (error) {
      setMessage(error.message);
    } finally {
      setBusy(false);
    }
  }

  function updateDiscoveryField(key, value) {
    setDiscoveryForm((current) => ({ ...current, [key]: value }));
  }

  return (
    <section id="sources" className="re-panel">
      <div className="re-section-head">
        <div>
          <h2>Lead source map</h2>
          <p>Real lead intake uses official APIs, public downloads, licensed feeds, or approved provider endpoints. Zillow and auction portals stay API/permission-only.</p>
        </div>
      </div>
      <div className="re-card-grid four">
        <article className="re-metric"><span>States mapped</span><strong>{catalog?.summary?.stateCount || states.length}</strong></article>
        <article className="re-metric"><span>National sources</span><strong>{catalog?.summary?.nationalSourceCount || sources.length}</strong></article>
        <article className="re-metric"><span>Pulling now</span><strong>{liveSources.length}</strong></article>
        <article className="re-metric"><span>Discovery seeds</span><strong>{catalog?.summary?.publicDiscoveryQueryCount || 0}</strong></article>
      </div>
      <form className="re-inline-form discovery" onSubmit={runDiscovery}>
        <label>Public search<input value={discoveryForm.query} onChange={(event) => updateDiscoveryField("query", event.target.value)} placeholder="tax sale, vacant building, land bank..." /></label>
        <label>State<input value={discoveryForm.state} onChange={(event) => updateDiscoveryField("state", event.target.value)} placeholder="GA" /></label>
        <label>City<input value={discoveryForm.city} onChange={(event) => updateDiscoveryField("city", event.target.value)} placeholder="Atlanta" /></label>
        <label>County<input value={discoveryForm.county} onChange={(event) => updateDiscoveryField("county", event.target.value)} placeholder="Fulton" /></label>
        <button type="submit" disabled={busy}>{busy ? "Searching..." : "Search public datasets"}</button>
      </form>
      {message && <p className="re-error">{message}</p>}
      {discovery && (
        <div className="re-discovery-list">
          <div className="re-section-head compact">
            <div>
              <h3>Discovery results</h3>
              <p>{discovery.summary?.datasetCount || 0} public dataset candidates for "{discovery.query}".</p>
            </div>
          </div>
          {(discovery.datasets || []).map((dataset) => (
            <article key={`${dataset.discoverySource}-${dataset.datasetId || dataset.landingPage}`}>
              <strong>{dataset.title}</strong>
              <span>{dataset.discoverySource} - relevance {dataset.relevanceScore ?? "-"} - {dataset.organization || "organization not listed"} - {dataset.format || "format not listed"}</span>
              <p>{dataset.description || "No description returned."}</p>
              <div className="re-actions">
                {dataset.landingPage && <a href={dataset.landingPage} target="_blank" rel="noreferrer">Open listing</a>}
                {dataset.apiOrDownloadUrl && <a href={dataset.apiOrDownloadUrl} target="_blank" rel="noreferrer">Open API/download</a>}
              </div>
            </article>
          ))}
          {discovery.nextStep && <p className="re-muted">{discovery.nextStep}</p>}
        </div>
      )}
      <div className="re-source-grid">
        {sources.map((source) => (
          <article className="re-source" key={source.sourceKey}>
            <div className="re-provider-card-head">
              <strong>{source.displayName}</strong>
              <span className={source.configured ? "re-status-good" : "re-status-warn"}>{source.integrationStatus}</span>
            </div>
            <p>{source.notes}</p>
            {source.env?.length > 0 && <em>Env: {source.env.join(", ")}</em>}
          </article>
        ))}
      </div>
      <details className="re-details">
        <summary>50-state county/source discovery map</summary>
        <div className="re-state-grid">
          {states.map((state) => (
            <article key={state.abbr}>
              <strong>{state.abbr}</strong>
              <span>{state.name}</span>
              <em>{state.prioritySourceFamilies?.slice(0, 3).join(" / ")}</em>
            </article>
          ))}
        </div>
      </details>
    </section>
  );
}

function MaterialsSearch({ provider, onProviderChange }) {
  const [query, setQuery] = useState("waterproof lvp flooring 20 mil case");
  const [zip, setZip] = useState("30310");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");

  async function search(event) {
    event.preventDefault();
    setBusy(true);
    setMessage("");
    try {
      const data = await fetchJson("/api/real-estate/materials/search", {
        method: "POST",
        body: JSON.stringify({ provider, query, zip, limit: 8 }),
      });
      setResult(data);
    } catch (error) {
      setMessage(error.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="re-panel">
      <div className="re-section-head">
        <div>
          <h2>Material lookup</h2>
          <p>Search Lowe's, Home Depot Pro, Ferguson, a generic vendor, or the estimating seed catalog.</p>
        </div>
      </div>
      <form className="re-inline-form" onSubmit={search}>
        <label>Provider<select value={provider} onChange={(event) => onProviderChange(event.target.value)}><option value="lowes">Lowe's</option><option value="home_depot_pro">Home Depot Pro</option><option value="ferguson">Ferguson</option><option value="generic_vendor">Generic vendor</option><option value="seed_catalog">Seed catalog</option></select></label>
        <label>Search<input value={query} onChange={(event) => setQuery(event.target.value)} /></label>
        <label>ZIP<input value={zip} onChange={(event) => setZip(event.target.value)} /></label>
        <button type="submit" disabled={busy}>{busy ? "Searching..." : "Search items"}</button>
      </form>
      {message && <p className="re-error">{message}</p>}
      {result && (
        <div className="re-product-grid">
          {(result.products || []).map((product) => (
            <article key={`${product.provider}-${product.providerProductId || product.title}`}>
              <strong>{product.title}</strong>
              <span>{product.provider} · item {product.providerSku || product.providerProductId || "not returned"}</span>
              <p>{product.brand || "Brand not returned"} · {product.unit || "each"} · {dollars(product.price)}</p>
              {product.notes && <em>{product.notes}</em>}
            </article>
          ))}
        </div>
      )}
    </section>
  );
}

function RealEstateBusinessApp() {
  const [authenticated, setAuthenticated] = useState(false);
  const [sessionChecked, setSessionChecked] = useState(false);
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [projects, setProjects] = useState([]);
  const [targetMarkets, setTargetMarkets] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [providerCapabilities, setProviderCapabilities] = useState(null);
  const [dataSources, setDataSources] = useState(null);
  const [leadSources, setLeadSources] = useState(null);
  const [documents, setDocuments] = useState(null);
  const [scheduledStatus, setScheduledStatus] = useState(null);
  const [ownerResearchSourcePlan, setOwnerResearchSourcePlan] = useState(null);
  const [ownerResearchReports, setOwnerResearchReports] = useState([]);
  const [ownerResearchReport, setOwnerResearchReport] = useState(null);
  const [ownerWatchlist, setOwnerWatchlist] = useState([]);
  const [taxSaleAuctionPlans, setTaxSaleAuctionPlans] = useState([]);
  const [taxSaleAuctionPlan, setTaxSaleAuctionPlan] = useState(null);
  const [scan, setScan] = useState(null);
  const [texasDealHunt, setTexasDealHunt] = useState(null);
  const [provider, setProvider] = useState("lowes");
  const [cartDraft, setCartDraft] = useState(null);
  const [cartExport, setCartExport] = useState(null);
  const [form, setForm] = useState({
    street: "",
    city: "Houston",
    state: "TX",
    zip: "",
    beds: "3",
    baths: "2",
    livingSqft: "1400",
    arv: "275000",
    acquisitionPrice: "125000",
    repairLevel: "medium",
    strategy: "flip",
  });
  const [market, setMarket] = useState({ type: "state", value: "TX", strategy: "flip" });
  const [targetMarketForm, setTargetMarketForm] = useState({ name: "Texas statewide", type: "state", value: "TX", strategy: "flip", scanLimit: 50, priority: 1 });
  const [ownerResearchForm, setOwnerResearchForm] = useState({
    street: "",
    city: "Pasadena",
    state: "TX",
    zip: "",
    ownerName: "",
    taxAccount: "",
    taxesOwed: "",
    sourceUrl: "",
  });
  const [taxSaleAuctionForm, setTaxSaleAuctionForm] = useState({
    street: "",
    city: "Houston",
    state: "TX",
    zip: "",
    county: "Harris",
    taxAccount: "",
    causeNumber: "",
    saleDate: "",
    redemptionClass: "unknown",
    occupancyStatus: "unknown",
    titleStatus: "not_reviewed",
    estimatedMarketValue: "",
    minimumBid: "",
    plannedBid: "",
    targetReturnRate: "15",
    repairBudget: "0",
    postJudgmentTaxes: "0",
    titleSearchBudget: "450",
    attorneyBudget: "750",
    possessionBudget: "1500",
    holdingMonths: "6",
    monthlyHoldingCost: "0",
    salesCostRate: "9",
    mortgageLienStatus: "unknown",
    mortgagePayoffEstimate: "0",
    federalTaxLienStatus: "unknown",
    federalTaxLienEstimate: "0",
    municipalLienStatus: "unknown",
    municipalLienEstimate: "0",
    hoaLienStatus: "unknown",
    hoaLienEstimate: "0",
    judgmentLienStatus: "unknown",
    judgmentLienEstimate: "0",
    otherSurvivingLienStatus: "unknown",
    otherSurvivingLienEstimate: "0",
    sourceUrl: "",
    notes: "",
  });
  const [manualComps, setManualComps] = useState("[]");

  const activeScope = selectedProject?.scope;
  const activeWarnings = useMemo(() => {
    if (!activeScope?.lines) return [];
    return activeScope.lines.flatMap((line) => [
      ...(line.warnings || []),
      ...(line.quoteWarnings || []),
    ]).filter(Boolean).slice(0, 8);
  }, [activeScope]);

  async function refreshCore() {
    const [capabilities, sources, leadSourceCatalog, documentLibrary, projectList, markets, schedule, ownerPlan, ownerReports, watchlist, taxSalePlans] = await Promise.all([
      fetchJson("/api/real-estate/provider-capabilities"),
      fetchJson("/api/real-estate/data-sources"),
      fetchJson("/api/real-estate/lead-sources"),
      fetchJson("/api/real-estate/documents"),
      fetchJson("/api/real-estate/projects"),
      fetchJson("/api/real-estate/target-markets?includeDisabled=true"),
      fetchJson("/api/real-estate/scheduled-scans/status"),
      fetchJson("/api/real-estate/owner-research/source-plan"),
      fetchJson("/api/real-estate/owner-research/reports?limit=10"),
      fetchJson("/api/real-estate/owner-watchlist?includeDisabled=true"),
      fetchJson("/api/real-estate/tax-sales/plans?limit=25"),
    ]);
    setProviderCapabilities(capabilities);
    setDataSources(sources);
    setLeadSources(leadSourceCatalog);
    setDocuments(documentLibrary);
    setProjects(projectList.projects || []);
    setTargetMarkets(markets.markets || []);
    setScheduledStatus(schedule);
    setOwnerResearchSourcePlan(ownerPlan);
    setOwnerResearchReports(ownerReports.reports || []);
    setOwnerWatchlist(watchlist.watchlist || []);
    setTaxSaleAuctionPlans(taxSalePlans.plans || []);
    if (!ownerResearchReport && ownerReports.reports?.[0]) setOwnerResearchReport(ownerReports.reports[0]);
    if (!taxSaleAuctionPlan && taxSalePlans.plans?.[0]) setTaxSaleAuctionPlan(taxSalePlans.plans[0]);
    if (!selectedProject && projectList.projects?.[0]) setSelectedProject(projectList.projects[0]);
  }

  useEffect(() => {
    fetchJson("/auth/site-session")
      .then((session) => setAuthenticated(Boolean(session.authenticated)))
      .catch(() => setAuthenticated(false))
      .finally(() => setSessionChecked(true));
  }, []);

  useEffect(() => {
    if (!authenticated) return;
    refreshCore().catch((error) => setMessage(error.message));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authenticated]);

  async function logout() {
    await fetchJson("/auth/site-logout", { method: "POST" }).catch(() => null);
    setAuthenticated(false);
  }

  function updateForm(key, value) {
    setForm((current) => ({ ...current, [key]: value }));
  }

  function updateOwnerResearchForm(key, value) {
    setOwnerResearchForm((current) => ({ ...current, [key]: value }));
  }

  function updateTaxSaleAuctionForm(key, value) {
    setTaxSaleAuctionForm((current) => ({ ...current, [key]: value }));
  }

  async function createLead(event) {
    event.preventDefault();
    setBusy(true);
    setMessage("");
    setCartDraft(null);
    try {
      const data = await fetchJson("/api/real-estate/projects", {
        method: "POST",
        body: JSON.stringify({
          address: { street: form.street, city: form.city, state: form.state, zip: form.zip },
          propertyFacts: {
            beds: Number(form.beds),
            baths: Number(form.baths),
            livingSqft: Number(form.livingSqft),
            arv: Number(form.arv),
            acquisitionPrice: Number(form.acquisitionPrice),
          },
          conditionProfile: { repairLevel: form.repairLevel },
          strategy: form.strategy,
          source: "real_estate_ui",
        }),
      });
      setSelectedProject(data.project);
      await refreshCore();
      setMessage("Project created with recommended SOW and deal math.");
    } catch (error) {
      setMessage(error.message);
    } finally {
      setBusy(false);
    }
  }

  async function quoteScope() {
    if (!selectedProject?.projectId) return;
    setBusy(true);
    setMessage("");
    try {
      const data = await fetchJson("/api/real-estate/scope/quote", {
        method: "POST",
        body: JSON.stringify({ projectId: selectedProject.projectId, provider, zip: selectedProject.address?.zip }),
      });
      const updated = { ...selectedProject, scope: data.scope, dealMath: { ...selectedProject.dealMath, repairs: data.scope.summary.estimatedTotal } };
      setSelectedProject(updated);
      await refreshCore();
      setMessage("Scope quoted. Live retailer item numbers appear only when the configured provider returns them.");
    } catch (error) {
      setMessage(error.message);
    } finally {
      setBusy(false);
    }
  }

  async function createCartDraft() {
    if (!selectedProject?.projectId) return;
    setBusy(true);
    setMessage("");
    try {
      const data = await fetchJson("/api/real-estate/vendors/cart-draft", {
        method: "POST",
        body: JSON.stringify({ projectId: selectedProject.projectId, preferredProvider: provider }),
      });
      setCartDraft(data.cartDraft);
      setCartExport(null);
      setMessage("Cart draft created. It requires human review and does not place a live order.");
    } catch (error) {
      setMessage(error.message);
    } finally {
      setBusy(false);
    }
  }

  async function runScan(event) {
    event.preventDefault();
    setBusy(true);
    setMessage("");
    try {
      const data = await fetchJson("/api/real-estate/markets/scan", {
        method: "POST",
        body: JSON.stringify({ market: { type: market.type, value: market.value }, strategy: market.strategy, limit: 25 }),
      });
      setScan(data.scan);
      setMessage("Market scan complete. Connect MLS/auction/county feeds to expand results beyond public REO data.");
    } catch (error) {
      setMessage(error.message);
    } finally {
      setBusy(false);
    }
  }

  async function createJobFromCandidate(candidate) {
    setBusy(true);
    setMessage("");
    try {
      const attrs = candidate.rawAttributes || {};
      const underwriting = candidate.underwriting || {};
      const facts = underwriting.propertyFacts || {};
      const data = await fetchJson("/api/real-estate/projects", {
        method: "POST",
        body: JSON.stringify({
          address: candidate.address,
          propertyFacts: {
            beds: facts.beds || attrs.beds || attrs.BEDS || attrs.BEDROOMS || form.beds,
            baths: facts.baths || attrs.baths || attrs.BATHS || attrs.BATHROOMS || form.baths,
            livingSqft: facts.livingSqft || attrs.livingSqft || attrs.SQFT || attrs.LIVING_SQFT || form.livingSqft,
            arv: facts.arv || underwriting.arv || form.arv,
            acquisitionPrice: facts.acquisitionPrice || underwriting.acquisitionPrice || candidate.listPrice || attrs.openingBid || attrs.assessedValue || form.acquisitionPrice,
          },
          conditionProfile: { repairLevel: "heavy", knownRisks: ["Unverified auction/public-source lead"] },
          strategy: candidate.dealType?.includes("TAX") || candidate.dealType?.includes("AUCTION") ? "wholesale" : "flip",
          source: candidate.source || "texas_deal_hunt",
          sourceUrl: candidate.sourceUrl || null,
          acquisitionChannel: candidate.dealType || "public_source",
          priority: "review_now",
          notes: `Created from Texas deal hunt. Candidate score ${candidate.score || "-"}; status ${candidate.status || "-"}; source ${candidate.source || "-"}. Verify title, taxes, liens, access, auction rules, redemption risk, repairs, and comps before offer/bid.`,
          tags: ["texas", "first_deal_hunt", candidate.dealType || "lead"].filter(Boolean),
        }),
      });
      setSelectedProject(data.project);
      await refreshCore();
      setMessage("Job created from Texas candidate. Verify records and deal math before any offer or bid.");
    } catch (error) {
      setMessage(error.message);
    } finally {
      setBusy(false);
    }
  }

  function ownerResearchPayload() {
    return {
      address: {
        street: ownerResearchForm.street,
        city: ownerResearchForm.city,
        state: ownerResearchForm.state,
        zip: ownerResearchForm.zip,
      },
      ownerName: ownerResearchForm.ownerName,
      taxAccount: ownerResearchForm.taxAccount,
      taxesOwed: ownerResearchForm.taxesOwed ? Number(ownerResearchForm.taxesOwed) : null,
      sourceUrl: ownerResearchForm.sourceUrl,
    };
  }

  async function runOwnerResearch(event) {
    event.preventDefault();
    setBusy(true);
    setMessage("");
    try {
      const data = await fetchJson("/api/real-estate/owner-research", {
        method: "POST",
        body: JSON.stringify(ownerResearchPayload()),
      });
      setOwnerResearchReport(data.report);
      await refreshCore();
      setMessage("Owner research report created. Verify each clue before contact, bid, or offer.");
    } catch (error) {
      setMessage(error.message);
    } finally {
      setBusy(false);
    }
  }

  async function saveOwnerMonitor() {
    setBusy(true);
    setMessage("");
    try {
      await fetchJson("/api/real-estate/owner-watchlist", {
        method: "POST",
        body: JSON.stringify(ownerResearchPayload()),
      });
      await refreshCore();
      setMessage("Owner/property monitor saved.");
    } catch (error) {
      setMessage(error.message);
    } finally {
      setBusy(false);
    }
  }

  async function runOwnerWatchlist() {
    setBusy(true);
    setMessage("");
    try {
      const data = await fetchJson("/api/real-estate/owner-watchlist/run", {
        method: "POST",
        body: JSON.stringify({ limit: 25 }),
      });
      if (data.reports?.[0]) setOwnerResearchReport(data.reports[0]);
      await refreshCore();
      setMessage(`Owner monitors complete: ${data.reportCount || 0} reports from ${data.watchCount || 0} watched leads.`);
    } catch (error) {
      setMessage(error.message);
    } finally {
      setBusy(false);
    }
  }

  async function createJobFromOwnerReport(report) {
    if (!report?.address) return;
    setBusy(true);
    setMessage("");
    try {
      const data = await fetchJson("/api/real-estate/projects", {
        method: "POST",
        body: JSON.stringify({
          address: report.address,
          propertyFacts: {},
          conditionProfile: {
            repairLevel: "heavy",
            knownRisks: ["Owner research lead; verify death/probate, title, taxes, redemption, occupancy, and possession before offer or bid."],
          },
          strategy: "wholesale",
          source: "owner_research",
          sourceUrl: report.sourceUrl || null,
          acquisitionChannel: "owner_research_tax_probate",
          priority: "review_now",
          notes: `Owner research report ${report.reportId}. Owner: ${report.ownerName || "not entered"}. Death status: ${report.summary?.ownerDeathStatus || "UNCONFIRMED"}. Tax status: ${report.summary?.taxDistressStatus || "UNKNOWN"}. Taxes entered: ${report.taxesOwed || "not entered"}.`,
          tags: ["owner_research", "tax_sale_risk", "probate_check", report.address?.state || "TX"].filter(Boolean),
        }),
      });
      setSelectedProject(data.project);
      await refreshCore();
      setMessage("Job created from owner research report. Verify title, probate, tax, occupancy, and redemption risk before action.");
    } catch (error) {
      setMessage(error.message);
    } finally {
      setBusy(false);
    }
  }

  function taxSaleAuctionPayload() {
    return {
      address: {
        street: taxSaleAuctionForm.street,
        city: taxSaleAuctionForm.city,
        state: taxSaleAuctionForm.state,
        zip: taxSaleAuctionForm.zip,
        county: taxSaleAuctionForm.county,
      },
      county: taxSaleAuctionForm.county,
      state: taxSaleAuctionForm.state,
      taxAccount: taxSaleAuctionForm.taxAccount,
      causeNumber: taxSaleAuctionForm.causeNumber,
      saleDate: taxSaleAuctionForm.saleDate,
      redemptionClass: taxSaleAuctionForm.redemptionClass,
      occupancyStatus: taxSaleAuctionForm.occupancyStatus,
      titleStatus: taxSaleAuctionForm.titleStatus,
      estimatedMarketValue: taxSaleAuctionForm.estimatedMarketValue ? Number(taxSaleAuctionForm.estimatedMarketValue) : null,
      minimumBid: taxSaleAuctionForm.minimumBid ? Number(taxSaleAuctionForm.minimumBid) : null,
      plannedBid: taxSaleAuctionForm.plannedBid ? Number(taxSaleAuctionForm.plannedBid) : null,
      targetReturnRate: taxSaleAuctionForm.targetReturnRate ? Number(taxSaleAuctionForm.targetReturnRate) : 15,
      repairBudget: taxSaleAuctionForm.repairBudget ? Number(taxSaleAuctionForm.repairBudget) : 0,
      postJudgmentTaxes: taxSaleAuctionForm.postJudgmentTaxes ? Number(taxSaleAuctionForm.postJudgmentTaxes) : 0,
      titleSearchBudget: taxSaleAuctionForm.titleSearchBudget ? Number(taxSaleAuctionForm.titleSearchBudget) : 0,
      attorneyBudget: taxSaleAuctionForm.attorneyBudget ? Number(taxSaleAuctionForm.attorneyBudget) : 0,
      possessionBudget: taxSaleAuctionForm.possessionBudget ? Number(taxSaleAuctionForm.possessionBudget) : 0,
      holdingMonths: taxSaleAuctionForm.holdingMonths ? Number(taxSaleAuctionForm.holdingMonths) : 0,
      monthlyHoldingCost: taxSaleAuctionForm.monthlyHoldingCost ? Number(taxSaleAuctionForm.monthlyHoldingCost) : 0,
      salesCostRate: taxSaleAuctionForm.salesCostRate ? Number(taxSaleAuctionForm.salesCostRate) : 9,
      mortgageLienStatus: taxSaleAuctionForm.mortgageLienStatus,
      mortgagePayoffEstimate: taxSaleAuctionForm.mortgagePayoffEstimate ? Number(taxSaleAuctionForm.mortgagePayoffEstimate) : 0,
      federalTaxLienStatus: taxSaleAuctionForm.federalTaxLienStatus,
      federalTaxLienEstimate: taxSaleAuctionForm.federalTaxLienEstimate ? Number(taxSaleAuctionForm.federalTaxLienEstimate) : 0,
      municipalLienStatus: taxSaleAuctionForm.municipalLienStatus,
      municipalLienEstimate: taxSaleAuctionForm.municipalLienEstimate ? Number(taxSaleAuctionForm.municipalLienEstimate) : 0,
      hoaLienStatus: taxSaleAuctionForm.hoaLienStatus,
      hoaLienEstimate: taxSaleAuctionForm.hoaLienEstimate ? Number(taxSaleAuctionForm.hoaLienEstimate) : 0,
      judgmentLienStatus: taxSaleAuctionForm.judgmentLienStatus,
      judgmentLienEstimate: taxSaleAuctionForm.judgmentLienEstimate ? Number(taxSaleAuctionForm.judgmentLienEstimate) : 0,
      otherSurvivingLienStatus: taxSaleAuctionForm.otherSurvivingLienStatus,
      otherSurvivingLienEstimate: taxSaleAuctionForm.otherSurvivingLienEstimate ? Number(taxSaleAuctionForm.otherSurvivingLienEstimate) : 0,
      sourceUrl: taxSaleAuctionForm.sourceUrl,
      notes: taxSaleAuctionForm.notes,
    };
  }

  async function analyzeTaxSaleAuction(event) {
    event.preventDefault();
    setBusy(true);
    setMessage("");
    try {
      const data = await fetchJson("/api/real-estate/tax-sales/analyze", {
        method: "POST",
        body: JSON.stringify(taxSaleAuctionPayload()),
      });
      setTaxSaleAuctionPlan(data.plan);
      setMessage("Auction economics calculated. Verify redemption class, title, possession, and taxes before trusting the bid cap.");
    } catch (error) {
      setMessage(error.message);
    } finally {
      setBusy(false);
    }
  }

  async function saveTaxSaleAuction() {
    setBusy(true);
    setMessage("");
    try {
      const data = await fetchJson("/api/real-estate/tax-sales/plans", {
        method: "POST",
        body: JSON.stringify({ ...taxSaleAuctionPayload(), planId: taxSaleAuctionPlan?.planId }),
      });
      setTaxSaleAuctionPlan(data.plan);
      await refreshCore();
      setMessage("Auction plan saved to the tax-sale portfolio.");
    } catch (error) {
      setMessage(error.message);
    } finally {
      setBusy(false);
    }
  }

  async function updateTaxSaleAuctionStatus(planId, status) {
    setBusy(true);
    setMessage("");
    try {
      const data = await fetchJson(`/api/real-estate/tax-sales/plans/${encodeURIComponent(planId)}`, {
        method: "PATCH",
        body: JSON.stringify({ status }),
      });
      setTaxSaleAuctionPlan(data.plan);
      await refreshCore();
      setMessage("Auction status updated.");
    } catch (error) {
      setMessage(error.message);
    } finally {
      setBusy(false);
    }
  }

  function loadTaxSaleAuctionPlan(plan) {
    if (!plan) return;
    setTaxSaleAuctionPlan(plan);
    const lienItem = (key) => (plan.lienRisk?.items || []).find((item) => item.key === key) || {};
    setTaxSaleAuctionForm({
      street: plan.address?.street || "",
      city: plan.address?.city || "",
      state: plan.address?.state || "TX",
      zip: plan.address?.zip || "",
      county: plan.county || plan.address?.county || "Harris",
      taxAccount: plan.taxAccount || "",
      causeNumber: plan.causeNumber || "",
      saleDate: plan.saleDate || "",
      redemptionClass: plan.redemptionRule?.redemptionClass || "unknown",
      occupancyStatus: plan.occupancyStatus || "unknown",
      titleStatus: plan.titleStatus || "not_reviewed",
      estimatedMarketValue: plan.assumptions?.estimatedMarketValue != null ? String(plan.assumptions.estimatedMarketValue) : "",
      minimumBid: plan.assumptions?.minimumBid != null ? String(plan.assumptions.minimumBid) : "",
      plannedBid: plan.assumptions?.plannedBid != null ? String(plan.assumptions.plannedBid) : "",
      targetReturnRate: plan.assumptions?.targetReturnRate != null ? String(plan.assumptions.targetReturnRate * 100) : "15",
      repairBudget: plan.costs?.detail?.repairBudget != null ? String(plan.costs.detail.repairBudget) : "0",
      postJudgmentTaxes: plan.costs?.detail?.postJudgmentTaxes != null ? String(plan.costs.detail.postJudgmentTaxes) : "0",
      titleSearchBudget: plan.costs?.detail?.titleSearchBudget != null ? String(plan.costs.detail.titleSearchBudget) : "450",
      attorneyBudget: plan.costs?.detail?.attorneyBudget != null ? String(plan.costs.detail.attorneyBudget) : "750",
      possessionBudget: plan.costs?.detail?.possessionBudget != null ? String(plan.costs.detail.possessionBudget) : "1500",
      holdingMonths: plan.assumptions?.holdingMonths != null ? String(plan.assumptions.holdingMonths) : "6",
      monthlyHoldingCost: plan.assumptions?.monthlyHoldingCost != null ? String(plan.assumptions.monthlyHoldingCost) : "0",
      salesCostRate: plan.assumptions?.salesCostRate != null ? String(plan.assumptions.salesCostRate * 100) : "9",
      mortgageLienStatus: lienItem("mortgage").status || "unknown",
      mortgagePayoffEstimate: lienItem("mortgage").estimatedPayoff != null ? String(lienItem("mortgage").estimatedPayoff) : "0",
      federalTaxLienStatus: lienItem("federal_tax").status || "unknown",
      federalTaxLienEstimate: lienItem("federal_tax").estimatedPayoff != null ? String(lienItem("federal_tax").estimatedPayoff) : "0",
      municipalLienStatus: lienItem("municipal_code").status || "unknown",
      municipalLienEstimate: lienItem("municipal_code").estimatedPayoff != null ? String(lienItem("municipal_code").estimatedPayoff) : "0",
      hoaLienStatus: lienItem("hoa").status || "unknown",
      hoaLienEstimate: lienItem("hoa").estimatedPayoff != null ? String(lienItem("hoa").estimatedPayoff) : "0",
      judgmentLienStatus: lienItem("judgment").status || "unknown",
      judgmentLienEstimate: lienItem("judgment").estimatedPayoff != null ? String(lienItem("judgment").estimatedPayoff) : "0",
      otherSurvivingLienStatus: lienItem("other").status || "unknown",
      otherSurvivingLienEstimate: lienItem("other").estimatedPayoff != null ? String(lienItem("other").estimatedPayoff) : "0",
      sourceUrl: plan.sourceUrl || "",
      notes: plan.notes || "",
    });
    setMessage("Auction plan loaded into the calculator.");
  }

  async function createProjectFromTaxSaleAuction(planId) {
    setBusy(true);
    setMessage("");
    try {
      const data = await fetchJson(`/api/real-estate/tax-sales/plans/${encodeURIComponent(planId)}/project`, {
        method: "POST",
      });
      setSelectedProject(data.project);
      await refreshCore();
      setMessage("Project created from tax sale auction plan.");
    } catch (error) {
      setMessage(error.message);
    } finally {
      setBusy(false);
    }
  }

  async function runTexasDealHunt() {
    setBusy(true);
    setMessage("");
    try {
      const data = await fetchJson("/api/real-estate/texas/deal-hunt", {
        method: "POST",
        body: JSON.stringify({ includeLiveSources: true, limitPerMarket: 25, discoveryLimit: 5 }),
      });
      setTexasDealHunt(data.report);
      setScheduledStatus((current) => ({ ...(current || {}), lastRun: data.report }));
      setScan(data.report?.candidates?.length ? { candidates: data.report.candidates, warnings: data.report.scans?.flatMap((item) => item.warnings || []) || [] } : null);
      setMessage(`Texas deal hunt complete: ${data.report.candidateCount} live-source candidates and ${data.report.manualSourceLinks?.length || 0} manual sources.`);
    } catch (error) {
      setMessage(error.message);
    } finally {
      setBusy(false);
    }
  }

  async function updateWorkflowItem(itemId, patch) {
    if (!selectedProject?.projectId) return;
    setBusy(true);
    setMessage("");
    try {
      const data = await fetchJson(`/api/real-estate/projects/${selectedProject.projectId}/workflow/items/${encodeURIComponent(itemId)}`, {
        method: "PATCH",
        body: JSON.stringify(patch),
      });
      setSelectedProject(data.project);
      await refreshCore();
      setMessage("Job checklist updated.");
    } catch (error) {
      setMessage(error.message);
    } finally {
      setBusy(false);
    }
  }

  async function saveTargetMarket(event) {
    event.preventDefault();
    setBusy(true);
    setMessage("");
    try {
      await fetchJson("/api/real-estate/target-markets", {
        method: "POST",
        body: JSON.stringify({
          name: targetMarketForm.name,
          type: targetMarketForm.type,
          value: targetMarketForm.value,
          strategy: targetMarketForm.strategy,
          scanLimit: Number(targetMarketForm.scanLimit),
          priority: Number(targetMarketForm.priority),
        }),
      });
      await refreshCore();
      setMessage("Target market saved.");
    } catch (error) {
      setMessage(error.message);
    } finally {
      setBusy(false);
    }
  }

  async function runTargetMarketScan(sendMessage = true) {
    setBusy(true);
    setMessage("");
    try {
      const data = await fetchJson("/api/real-estate/markets/scan-targets", {
        method: "POST",
        body: JSON.stringify({ includeLiveSources: true, source: "real_estate_ui_target_market_batch" }),
      });
      setScheduledStatus((current) => ({ ...(current || {}), lastRun: data.report }));
      setScan(data.report?.scans?.[0] ? { ...data.report.scans[0], candidates: data.report.topCandidates || [] } : null);
      if (sendMessage) setMessage(`Target-market batch complete: ${data.report.totalCandidates} candidates across ${data.report.scanCount} scans.`);
    } catch (error) {
      setMessage(error.message);
    } finally {
      setBusy(false);
    }
  }

  async function runScheduledNow() {
    setBusy(true);
    setMessage("");
    try {
      const data = await fetchJson("/api/real-estate/scheduled-scans/run", { method: "POST" });
      setScheduledStatus((current) => ({ ...(current || {}), lastRun: data.report }));
      if (data.report?.reportType === "texas_first_deal_hunt") setTexasDealHunt(data.report);
      setMessage(`Scheduled scan run complete: ${data.report?.totalCandidates || 0} candidates.`);
    } catch (error) {
      setMessage(error.message);
    } finally {
      setBusy(false);
    }
  }

  async function updateScopeLine(scopeLineId, draft) {
    if (!selectedProject?.projectId) return;
    setBusy(true);
    setMessage("");
    try {
      const data = await fetchJson(`/api/real-estate/projects/${selectedProject.projectId}/scope/lines/${scopeLineId}`, {
        method: "PATCH",
        body: JSON.stringify({
          quantity: Number(draft.quantity),
          materialOrderQuantity: Number(draft.materialOrderQuantity),
          materialUnitCost: Number(draft.materialUnitCost),
          laborUnitCost: Number(draft.laborUnitCost),
          notes: draft.notes,
        }),
      });
      setSelectedProject(data.project);
      await refreshCore();
      setMessage("Scope line saved.");
    } catch (error) {
      setMessage(error.message);
    } finally {
      setBusy(false);
    }
  }

  async function approveMaterial(scopeLineId) {
    if (!selectedProject?.projectId) return;
    setBusy(true);
    setMessage("");
    try {
      const data = await fetchJson(`/api/real-estate/projects/${selectedProject.projectId}/scope/lines/${scopeLineId}/approve-material`, {
        method: "POST",
        body: JSON.stringify({ optionIndex: 0 }),
      });
      setSelectedProject(data.project);
      await refreshCore();
      setMessage("Material approved for that scope line.");
    } catch (error) {
      setMessage(error.message);
    } finally {
      setBusy(false);
    }
  }

  async function refreshComps() {
    if (!selectedProject?.projectId) return;
    setBusy(true);
    setMessage("");
    try {
      const comps = JSON.parse(manualComps || "[]");
      const data = await fetchJson(`/api/real-estate/projects/${selectedProject.projectId}/comps`, {
        method: "POST",
        body: JSON.stringify({ comps, fetchProvider: comps.length === 0, provider: "generic_property_feed" }),
      });
      setSelectedProject(data.project);
      await refreshCore();
      setMessage(`Comp report updated with ${data.report.summary.usedCompCount} usable comps.`);
    } catch (error) {
      setMessage(error.message);
    } finally {
      setBusy(false);
    }
  }

  async function exportCartDraft() {
    if (!cartDraft?.cartDraftId) return;
    setBusy(true);
    setMessage("");
    try {
      const data = await fetchJson(`/api/real-estate/vendors/cart-draft/${cartDraft.cartDraftId}/export`);
      setCartExport(data);
      setMessage("Cart export prepared for human review.");
    } catch (error) {
      setMessage(error.message);
    } finally {
      setBusy(false);
    }
  }

  if (!sessionChecked) return <main className="re-loading">Checking admin session...</main>;
  if (!authenticated) return <LoginPage onLogin={setAuthenticated} />;

  return (
    <div className="re-shell">
      <aside className="re-sidebar">
        <a className="re-brand" href="/real-estate">
          <span>RE</span>
          <div><strong>Interpres Real Estate</strong><small>Flip · Wholesale · SOW</small></div>
        </a>
        <nav>
          <a href="#lead">Lead Builder</a>
          <a href="#texas-hunt">Texas Deal Hunt</a>
          <a href="#owner-research">Owner Research</a>
          <a href="#tax-sale-desk">Tax Sale Desk</a>
          <a href="#documents">Documents</a>
          <a href="#targets">Target Markets</a>
          <a href="#sources">Lead Sources</a>
          <a href="#projects">Projects</a>
          <a href="#scope">Scope + Materials</a>
          <a href="#comps">Comps + ARV</a>
          <a href="#market">Morning Market Scan</a>
          <a href="#schedule">Schedule</a>
          <a href="#providers">Providers</a>
          <a href="/dashboard">Stock Analyst</a>
          <button onClick={logout}>Lock Site</button>
        </nav>
      </aside>

      <main className="re-main">
        <header className="re-hero">
          <div>
            <p className="re-kicker">New Interpres business module</p>
            <h1>Midwest Homes Group Deal Cockpit</h1>
            <p>Texas-first acquisition workflow for finding a deeply discounted first deal, creating a job, checking public records, building scope, and moving from lead to sold.</p>
          </div>
          <div className="re-hero-card">
            <span>First goal</span>
            <strong>Find and finish deal #1</strong>
            <p>Hard-money proof of funds, title checks, repair risk, and MAO discipline come before every bid.</p>
          </div>
        </header>

        {message && <p className={message.toLowerCase().includes("error") ? "re-error" : "re-info"}>{message}</p>}

        <section id="lead" className="re-panel">
          <div className="re-section-head">
            <div>
              <h2>Address to project</h2>
              <p>Type an address and create a full job: checklist, repair estimate, MAO, scope of work, comp area, and document trail.</p>
            </div>
          </div>
          <form className="re-lead-form" onSubmit={createLead}>
            <label>Street address<input value={form.street} onChange={(event) => updateForm("street", event.target.value)} placeholder="123 Main St" /></label>
            <label>City<input value={form.city} onChange={(event) => updateForm("city", event.target.value)} /></label>
            <label>State<input value={form.state} onChange={(event) => updateForm("state", event.target.value)} /></label>
            <label>ZIP<input value={form.zip} onChange={(event) => updateForm("zip", event.target.value)} /></label>
            <label>Beds<input value={form.beds} onChange={(event) => updateForm("beds", event.target.value)} /></label>
            <label>Baths<input value={form.baths} onChange={(event) => updateForm("baths", event.target.value)} /></label>
            <label>Sqft<input value={form.livingSqft} onChange={(event) => updateForm("livingSqft", event.target.value)} /></label>
            <label>ARV<input value={form.arv} onChange={(event) => updateForm("arv", event.target.value)} /></label>
            <label>Ask/Bid<input value={form.acquisitionPrice} onChange={(event) => updateForm("acquisitionPrice", event.target.value)} /></label>
            <label>Repair level<select value={form.repairLevel} onChange={(event) => updateForm("repairLevel", event.target.value)}><option value="light">Light</option><option value="medium">Medium</option><option value="heavy">Heavy</option><option value="gut">Gut</option></select></label>
            <label>Strategy<select value={form.strategy} onChange={(event) => updateForm("strategy", event.target.value)}><option value="flip">Fix & flip</option><option value="wholesale">Wholesale</option><option value="rental">Rental</option></select></label>
            <button type="submit" disabled={busy}>{busy ? "Building..." : "Create job + SOW"}</button>
          </form>
        </section>

        <TexasDealHunt report={texasDealHunt} onRun={runTexasDealHunt} onCreateJob={createJobFromCandidate} busy={busy} />

        <OwnerResearchPanel
          report={ownerResearchReport}
          reports={ownerResearchReports}
          sourcePlan={ownerResearchSourcePlan}
          watchlist={ownerWatchlist}
          form={ownerResearchForm}
          onFormChange={updateOwnerResearchForm}
          onRun={runOwnerResearch}
          onSaveMonitor={saveOwnerMonitor}
          onRunWatchlist={runOwnerWatchlist}
          onCreateJob={createJobFromOwnerReport}
          busy={busy}
        />

        <TaxSaleAuctionDesk
          form={taxSaleAuctionForm}
          onFormChange={updateTaxSaleAuctionForm}
          plan={taxSaleAuctionPlan}
          plans={taxSaleAuctionPlans}
          onAnalyze={analyzeTaxSaleAuction}
          onSave={saveTaxSaleAuction}
          onCreateProject={createProjectFromTaxSaleAuction}
          onStatusChange={updateTaxSaleAuctionStatus}
          onLoadPlan={loadTaxSaleAuctionPlan}
          busy={busy}
        />

        <DocumentLibrary library={documents} />

        <LeadSourceMap catalog={leadSources || dataSources?.leadSourceCatalog} />

        <section id="targets" className="re-panel split">
          <div>
            <div className="re-section-head">
              <div>
                <h2>Target markets</h2>
                <p>Saved ZIP/city/state targets drive batch scans and scheduled morning reports.</p>
              </div>
            </div>
            <form className="re-inline-form target" onSubmit={saveTargetMarket}>
              <label>Name<input value={targetMarketForm.name} onChange={(event) => setTargetMarketForm((current) => ({ ...current, name: event.target.value }))} /></label>
              <label>Type<select value={targetMarketForm.type} onChange={(event) => setTargetMarketForm((current) => ({ ...current, type: event.target.value }))}><option value="zip">ZIP</option><option value="city">City</option><option value="county">County</option><option value="state">State</option></select></label>
              <label>Value<input value={targetMarketForm.value} onChange={(event) => setTargetMarketForm((current) => ({ ...current, value: event.target.value }))} /></label>
              <label>Strategy<select value={targetMarketForm.strategy} onChange={(event) => setTargetMarketForm((current) => ({ ...current, strategy: event.target.value }))}><option value="wholesale">Wholesale</option><option value="flip">Flip</option><option value="rental">Rental</option></select></label>
              <label>Limit<input value={targetMarketForm.scanLimit} onChange={(event) => setTargetMarketForm((current) => ({ ...current, scanLimit: event.target.value }))} /></label>
              <label>Priority<input value={targetMarketForm.priority} onChange={(event) => setTargetMarketForm((current) => ({ ...current, priority: event.target.value }))} /></label>
              <button type="submit" disabled={busy}>{busy ? "Saving..." : "Save target"}</button>
            </form>
            <div className="re-target-list">
              {targetMarkets.map((target) => (
                <article key={target.targetMarketId}>
                  <strong>{target.name}</strong>
                  <span>{target.market?.type}: {target.market?.value} - {target.strategy} - limit {target.scanLimit}</span>
                  <em>{target.source === "env" ? "Env configured" : "Saved in app state"}</em>
                </article>
              ))}
              {!targetMarkets.length && <p className="re-muted">No target markets saved yet.</p>}
            </div>
          </div>
          <div>
            <h2>Batch controls</h2>
            <p className="re-muted">Run every saved target now, or let the disabled-by-default scheduler run when enabled by env.</p>
            <div className="re-actions vertical">
              <button type="button" disabled={busy} onClick={() => runTargetMarketScan(true)}>Run target batch now</button>
              <button type="button" disabled={busy} onClick={runScheduledNow}>Run scheduled scan now</button>
            </div>
            {scheduledStatus?.lastRun && (
              <article className="re-source">
                <strong>Latest batch</strong>
                <span>{formatDate(scheduledStatus.lastRun.completedAt)} - {scheduledStatus.lastRun.totalCandidates} candidates</span>
                <p>{scheduledStatus.lastRun.scanCount} scans across {scheduledStatus.lastRun.targetMarketCount} configured targets.</p>
              </article>
            )}
          </div>
        </section>

        <section id="projects" className="re-panel split">
          <div>
            <div className="re-section-head"><div><h2>Projects</h2><p>Select a property to review scope, quote materials, and draft carts.</p></div></div>
            <ProjectList projects={projects} selectedProjectId={selectedProject?.projectId} onSelect={setSelectedProject} />
          </div>
          <div>
            <h2>Active deal</h2>
            {selectedProject ? (
              <>
                <h3>{selectedProject.address?.full}</h3>
                <SummaryCards scope={activeScope} dealMath={selectedProject.dealMath} />
                <div className="re-card-grid three">
                  <article className="re-metric"><span>Workflow</span><strong>{selectedProject.workflow?.completion?.percent || 0}%</strong></article>
                  <article className="re-metric"><span>Funding</span><strong>{selectedProject.job?.proofOfFundsStatus || "needed"}</strong></article>
                  <article className="re-metric"><span>Channel</span><strong>{selectedProject.job?.acquisitionChannel || selectedProject.source}</strong></article>
                </div>
                <p className="re-muted">Formula: {selectedProject.dealMath?.formula}</p>
                <h3 className="re-subhead">Job checklist</h3>
                <WorkflowChecklist project={selectedProject} onUpdateItem={updateWorkflowItem} />
              </>
            ) : <p className="re-muted">Create or select a project.</p>}
          </div>
        </section>

        <section id="comps" className="re-panel split">
          <div>
            <div className="re-section-head">
              <div>
                <h2>Comps + ARV</h2>
                <p>Paste sold comps as JSON or connect a provider template. The app weights comps by similarity and updates MAO.</p>
              </div>
            </div>
            <textarea className="re-json-box" value={manualComps} onChange={(event) => setManualComps(event.target.value)} rows={8} />
            <div className="re-actions">
              <button type="button" disabled={!selectedProject || busy} onClick={refreshComps}>Refresh comp report</button>
            </div>
            <p className="re-muted">Example comp: {"[{\"address\":\"100 Main St\",\"salePrice\":210000,\"livingSqft\":1400,\"beds\":3,\"baths\":2,\"distanceMiles\":0.4,\"saleDate\":\"2026-05-01\"}]"}</p>
          </div>
          <div>
            <h2>Current ARV report</h2>
            {selectedProject?.compReport ? (
              <>
                <div className="re-card-grid three">
                  <article className="re-metric"><span>ARV low</span><strong>{dollars(selectedProject.compReport.summary?.arvLow)}</strong></article>
                  <article className="re-metric"><span>ARV mid</span><strong>{dollars(selectedProject.compReport.summary?.arvMid)}</strong></article>
                  <article className="re-metric"><span>Confidence</span><strong>{selectedProject.compReport.summary?.confidenceScore || 0}</strong></article>
                </div>
                <div className="re-target-list">
                  {(selectedProject.compReport.comps || []).map((comp) => (
                    <article key={comp.compId}>
                      <strong>{comp.address?.full || comp.compId}</strong>
                      <span>{dollars(comp.salePrice)} sold - adjusted {dollars(comp.adjustedPrice)}</span>
                      <em>Similarity {comp.similarityScore} / {comp.distanceMiles ?? "-"} miles</em>
                    </article>
                  ))}
                </div>
              </>
            ) : <p className="re-muted">No comp report yet. Paste comps or configure a provider template.</p>}
          </div>
        </section>

        <section id="scope" className="re-panel">
          <div className="re-section-head">
            <div>
              <h2>Scope of work + material quote</h2>
              <p>Quote line items against the selected provider. Seed estimates never pretend to be retailer SKUs.</p>
            </div>
            <div className="re-actions">
              <select value={provider} onChange={(event) => setProvider(event.target.value)}><option value="lowes">Lowe's</option><option value="home_depot_pro">Home Depot Pro</option><option value="ferguson">Ferguson</option><option value="generic_vendor">Generic vendor</option><option value="seed_catalog">Seed catalog</option></select>
              <button onClick={quoteScope} disabled={!selectedProject || busy}>Quote materials</button>
              <button onClick={createCartDraft} disabled={!selectedProject || busy}>Create cart draft</button>
            </div>
          </div>
          <ScopeTable scope={activeScope} />
          <h3 className="re-subhead">Room takeoff</h3>
          <RoomTakeoff scope={activeScope} />
          <h3 className="re-subhead">Editable scope and item approvals</h3>
          <ScopeEditorTable scope={activeScope} onUpdateLine={updateScopeLine} onApproveMaterial={approveMaterial} />
          {activeWarnings.length > 0 && <div className="re-warning-list">{activeWarnings.map((warning, index) => <span key={`${warning}-${index}`}>{warning}</span>)}</div>}
          {cartDraft && (
            <article className="re-cart-draft">
              <h3>Cart draft</h3>
              <p>{cartDraft.safetyMessage}</p>
              <div className="re-card-grid three">
                <article className="re-metric"><span>Status</span><strong>{cartDraft.status}</strong></article>
                <article className="re-metric"><span>Ready lines</span><strong>{cartDraft.totals.readyLineCount}</strong></article>
                <article className="re-metric"><span>Needs item choice</span><strong>{cartDraft.totals.needsItemSelectionCount}</strong></article>
              </div>
              <div className="re-actions">
                <button type="button" onClick={exportCartDraft} disabled={busy}>Export cart review</button>
              </div>
              {cartExport && <textarea className="re-json-box" readOnly rows={8} value={cartExport.csv || ""} />}
            </article>
          )}
        </section>

        <MaterialsSearch provider={provider} onProviderChange={setProvider} />

        <section id="market" className="re-panel split">
          <div>
            <div className="re-section-head"><div><h2>Morning market scan</h2><p>Scan by ZIP, city, or state. HUD and USDA are wired now; Zillow/Bridge, MLS, auction, and county feeds are approved-source adapter slots.</p></div></div>
            <form className="re-inline-form" onSubmit={runScan}>
              <label>Type<select value={market.type} onChange={(event) => setMarket((current) => ({ ...current, type: event.target.value }))}><option value="zip">ZIP</option><option value="city">City</option><option value="state">State</option></select></label>
              <label>Value<input value={market.value} onChange={(event) => setMarket((current) => ({ ...current, value: event.target.value }))} /></label>
              <label>Strategy<select value={market.strategy} onChange={(event) => setMarket((current) => ({ ...current, strategy: event.target.value }))}><option value="flip">Flip</option><option value="wholesale">Wholesale</option></select></label>
              <button type="submit" disabled={busy}>{busy ? "Scanning..." : "Run scan"}</button>
            </form>
            <CandidateList scan={scan} />
          </div>
          <div>
            <h2>Source plan</h2>
            {(dataSources?.publicSources || []).map((source) => (
              <article className="re-source" key={source.source}>
                <strong>{source.source}</strong>
                <span>{source.status}</span>
                <p>{source.use}</p>
              </article>
            ))}
          </div>
        </section>

        <section id="schedule" className="re-panel">
          <div className="re-section-head">
            <div>
              <h2>Scheduled morning reports</h2>
              <p>Server-side Texas deal scans are controlled by env, emailed to you, and saved here so you can create a job from any candidate worth underwriting.</p>
            </div>
            <div className="re-actions">
              <button type="button" disabled={busy} onClick={refreshCore}>Refresh report</button>
              <button type="button" disabled={busy} onClick={runScheduledNow}>Run now</button>
            </div>
          </div>
          <div className="re-card-grid four">
            <article className="re-metric"><span>Runtime</span><strong>{scheduledStatus?.policy?.enabled ? "On" : "Off"}</strong></article>
            <article className="re-metric"><span>Mode</span><strong>{scheduledStatus?.policy?.mode || "texas_first_deal"}</strong></article>
            <article className="re-metric"><span>Time</span><strong>{scheduledStatus?.policy?.time || "07:00"}</strong></article>
            <article className="re-metric"><span>Timezone</span><strong>{scheduledStatus?.policy?.timeZone || "America/New_York"}</strong></article>
          </div>
          <div className="re-card-grid four re-sub-metrics">
            <article className="re-metric"><span>Email report</span><strong>{scheduledStatus?.policy?.emailEnabled ? "On" : "Off"}</strong></article>
            <article className="re-metric"><span>Recipient</span><strong>{scheduledStatus?.policy?.emailTo ? "Configured" : "Missing"}</strong></article>
            <article className="re-metric"><span>In flight</span><strong>{scheduledStatus?.inFlight ? "Yes" : "No"}</strong></article>
            <article className="re-metric"><span>Latest email</span><strong>{scheduledStatus?.lastRun?.emailDelivery?.providerStatus || (scheduledStatus?.lastRun?.emailDelivery?.skipped ? "Skipped" : "-")}</strong></article>
          </div>
          {scheduledStatus?.lastError && <p className="re-error">{scheduledStatus.lastError.message}</p>}
          {scheduledStatus?.lastRun && (
            <>
              <article className="re-source">
                <strong>Latest Texas report</strong>
                <span>{formatDate(scheduledStatus.lastRun.completedAt)} - {scheduledStatus.lastRun.totalCandidates || scheduledStatus.lastRun.candidateCount || 0} candidates</span>
                <p>{scheduledStatus.lastRun.nextStep || "Review candidates, verify the numbers, and create jobs for leads worth pursuing."}</p>
              </article>
              <ReportCandidateCards
                candidates={(scheduledStatus.lastRun.topCandidates || scheduledStatus.lastRun.candidates || []).slice(0, 12)}
                onCreateJob={createJobFromCandidate}
                busy={busy}
                emptyMessage="No saved candidates in the latest scheduled report."
              />
            </>
          )}
        </section>

        <section id="providers" className="re-panel">
          <div className="re-section-head"><div><h2>Provider connections</h2><p>Connect official provider APIs or approved partner endpoints with environment templates.</p></div></div>
          <ProviderStatus providers={providerCapabilities?.providers || []} />
        </section>
      </main>
    </div>
  );
}

export default RealEstateBusinessApp;
